import { type LocaleType } from '@landsbankinn/l16n';
import React, { useContext, useMemo, type ReactNode } from 'react';
import {
  InitialStateServiceToken,
  type IInitialStateService,
} from 'src/services/InitialStateService';
import { ServicesProvider } from 'src/services/ServicesProvider';

/**
 * Allowed login types.
 * Should match https://git.lais.net/authentication/landsbanki-idp/blob/AUTH-298/Web/Models/LoginType.cs
 */
export enum AllowedLoginType {
  Password = 'password',
  PkiSim = 'pkiSim',
  AudkenniApp = 'audkenniApp',
  App = 'app',
}

interface IClient {
  clientId: string;
  clientName: Record<LocaleType | 'iv', string>;
  clientDescription?: Record<LocaleType, string>;
  landsbankiClient: boolean;
  isNativeAppClient: boolean;
  traceparent: string;
  uiTheme?: string; // currently only supports "TM"
}

interface IGraphQl {
  graphQlBaseUrl: string;
}

interface INonce {
  nonce?: string;
}

interface IErrors {
  errors?: string[];
}

interface IEnvironment {
  environment?: string;
}

interface ITraceparent {
  traceparent: string;
}

/**
 * The server state that is always provided.
 * See /Web/Models/InitialLoginState.cs for example
 */
export interface CommonServerState
  extends IClient, INonce, IErrors, IEnvironment, IGraphQl, ITraceparent {
  id?: string;
}

export type FormField = {
  name: string;
  value?: string;
  type: 'text' | 'tel' | 'password' | 'hidden';
  required: boolean;
  maxLength?: number;
  minLength?: number;
  maxValue?: any;
  minValue?: any;
  pattern?: string;
  debugValues?: DebugValue[];
};

export type DebugValue = {
  value: string;
  displayValue: string;
};

export type Form<T extends string> = {
  action: string;
  fields: Record<T | 'xsrf' | 'traceparent', FormField>;
  method: 'POST' | 'XMLHttpRequest';
};

export enum ChoiceType {
  None = 'none',
  Multiple = 'multiple',
  Single = 'single',
}

export enum ScopeDataFormat {
  Account = 'account',
  Kennitala = 'kennitala',
  CreditCard = 'creditCard',
  Number = 'number',
  Money = 'money',
  ExchangeRate = 'exchangeRate',
}

export enum ScopeDataType {
  String = 'string',
  StringArray = 'stringArray',
  Number = 'number',
  DateTime = 'dateTime',
}

export type ScopeData = {
  choiceType: ChoiceType;
  format?: ScopeDataFormat;
  hero?: boolean;
  name: string;
  type: ScopeDataType;
  value: any; //string | string[] | number | Date ;

  label?: string;
  hash?: boolean;
  group?: string;
  optional?: boolean;
  selected?: boolean;

  localized_name?: string;
  isLocalized: boolean;
};

export type ScopeLocalization = {
  is: Record<string, string>;
  en: Record<string, string>;
  pl?: Record<string, string>;
};

export type Account = {
  id: string;
  name: string;
  balance: number;
  availableFunds: number;
  currency: string;
};

export enum ScopeStatus {
  PendingUserInteraction = 'pendingUserInteraction',
  PendingData = 'pendingData',
  Ready = 'ready',
  Consented = 'consented',
  Rejected = 'rejected',
  Invalid = 'invalid',
  InvalidUser = 'invalidUser',
  Expired = 'expired',
  Success = 'success',
  Error = 'error',
}

export type Scope = {
  allowReject?: boolean;
  accounts: Record<string, Account>;
  callbackUrl: string;
  data: ScopeData[];
  fullName: string;
  status?: ScopeStatus;

  userName?: string;
  context?: string;
  hashedContext?: string;
  name: string;
  parameters: Record<string, string>;
  expires?: Date;
  hashAlgorithm?: string;
  localization?: ScopeLocalization;
  requiresSca: boolean;
  requiresConsent: boolean;
};

export type PasswordFormType = Form<'userName' | 'password'>;
export type SmsFormType = Form<'code'>;
export type PkiSimFormType = Form<'phoneNumber'>;
export type AudkenniAppFormType = Form<'kennitala'>;
export type AppFormType = Form<'id'>;
export type FormType =
  | PasswordFormType
  | SmsFormType
  | PkiSimFormType
  | AudkenniAppFormType
  | AppFormType;

/**
 * The login state provided by the server.
 * See /Web/Models/InitialLoginState.cs
 */
export interface LoginServerState extends CommonServerState {
  allowedLoginTypes: AllowedLoginType[];
  forms: {
    password: PasswordFormType;
    sms: SmsFormType;
    pkiSim: PkiSimFormType;
    audkenniApp: AudkenniAppFormType;
    app: AppFormType;
  };
  isConsentFlow?: boolean;
  customScopeName?: string;
}

export function isLoginServerState(
  state: ApplicationServerStateType,
): state is LoginServerState {
  return (
    (state as LoginServerState).forms !== undefined &&
    (state as LoginServerState).allowedLoginTypes !== undefined
  );
}

export type User = {
  userName: string;
  userType: 'retail' | 'corporate' | 'internal' | 'vendor' | 'national';
  name: string;
};

/**
 * SelectAccount state provided by the server
 * See /Web/Models/SelectAccountState.cs
 */
export interface SelectAccountServerState extends CommonServerState {
  users: User[];
  form: Form<'userName'>;
  subjectId: string;
  name: string;
  kennitala: string;
}

export function isSelectAccountServerState(
  state: ApplicationServerStateType,
): state is SelectAccountServerState {
  return (state as SelectAccountServerState).users !== undefined;
}

export interface ErrorServerState extends CommonServerState {
  id: string;
  error: string;
  errorDescription: string;
  traceparent: string;
}

export function isErrorServerState(
  state: ApplicationServerStateType,
): state is ErrorServerState {
  return (state as ErrorServerState).error !== undefined;
}

export interface ConsentServerState extends CommonServerState {
  scope: Scope;
  form: Form<'Consent'>;
}

export function isConsentServerState(
  state: ApplicationServerStateType,
): state is ConsentServerState {
  return (
    (state as ConsentServerState).scope !== undefined &&
    (state as ConsentServerState).form !== undefined
  );
}

// Application server state can be multiple interfaces
export type ApplicationServerStateType =
  | LoginServerState
  | SelectAccountServerState
  | ErrorServerState
  | ConsentServerState;

// The React Context type used in the application.
type ApplicationServerContextType = [ApplicationServerStateType];

/**
 * The React Context object that components subscribe to.
 * The default value for the context is the previously defined initial state.
 * See https://reactjs.org/docs/context.html#reactcreatecontext for more documentation.
 */
// NOTE: Avoid importing initialServerState directly here to prevent a circular
// dependency (server.tsx <-> InitialStateService.ts) which caused a runtime
// ReferenceError "Cannot access 'initialServerState' before initialization".
// We provide a lightweight fallback value; real state is injected by the
// ApplicationServerContextProvider via InitialStateService.
const fallbackServerState = {} as ApplicationServerStateType;
export const ApplicationServerContext =
  React.createContext<ApplicationServerContextType>([fallbackServerState]);

/**
 * A hook that returns the application context.
 * @returns The application context, current state and dispatch function
 */
export function useApplicationServerContext(): ApplicationServerContextType {
  return useContext(ApplicationServerContext);
}

function isValidState(state: ApplicationServerStateType): boolean {
  return Object.keys(state).length > 0;
}

/**
 * Component that allows consuming components to subscribe to context changes.
 * @param props Optional children components
 * @returns Application context provider
 */
export function ApplicationServerContextProvider(props: {
  children?: ReactNode;
}) {
  const state = useMemo(() => {
    const stateService = ServicesProvider.getSingleton<IInitialStateService>(
      InitialStateServiceToken,
    );
    return stateService.getInitialState();
  }, []);

  // During staging we might get an empty initial state
  // Instead of showing errors, just show a message without crashing the app
  if (!isValidState(state)) {
    return (
      <div>
        Invalid or empty state. If running in{' '}
        <strong>
          <code>staging environment</code>
        </strong>{' '}
        use with an IDP server using local UI, like{' '}
        <a href="https://authdevci-client-dev-auth.ci.landsbankinn.is/">
          IDP Test Client
        </a>
      </div>
    );
  }

  return (
    <ApplicationServerContext.Provider value={[state]}>
      {props.children}
    </ApplicationServerContext.Provider>
  );
}

declare global {
  interface Window {
    __INITIAL_SERVER_STATE__: ApplicationServerStateType;
  }
}
